import { TablePaginationConfig } from 'antd'
import { AccountVestingSearchParamsV2, AccountVestingTypeV2 } from 'Modules'
import { useCallback, useEffect, useState } from 'react'

import { accountVestingV2Api } from 'api/account-vesting-v2-api'
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'constants/pagination'
import { useAccountVestingContextV2 } from 'context/account-vesting-context-provider-v2'
import { mapContextTypeToApiType } from 'utils/account-vesting-v2'
import { logInfo } from 'utils/logger'

export const useAccountVestingDataV2 = () => {
  const { type } = useAccountVestingContextV2()

  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<AccountVestingTypeV2[]>([])
  const [total, setTotal] = useState(0)

  const defaultSearchParams: AccountVestingSearchParamsV2 = {
    type: type ? mapContextTypeToApiType(type) : 2,
    page: DEFAULT_PAGE_INDEX,
    limit: DEFAULT_PAGE_SIZE,
    keyword: '',
    status: null,
  }

  const [searchParams, setSearchParams] =
    useState<AccountVestingSearchParamsV2>(defaultSearchParams)

  const getData = useCallback(
    async (params?: AccountVestingSearchParamsV2) => {
      if (!type) return
      const apiType = mapContextTypeToApiType(type)
      const p: AccountVestingSearchParamsV2 = {
        ...(params || searchParams),
        type: apiType,
      }
      setLoading(true)
      try {
        const res = await accountVestingV2Api.getList(p)
        if (res?.data?.success) {
          const { results, totalResults } = res.data?.data || {}
          setData(results || [])
          setTotal(totalResults || 0)
        } else {
          setData([])
          setTotal(0)
        }
      } catch (error) {
        setData([])
        setTotal(0)
        logInfo('Error: ', error)
      } finally {
        setLoading(false)
      }
    },
    [type, searchParams]
  )

  const onSearch = useCallback(
    (input?: Partial<AccountVestingSearchParamsV2>) => {
      const newParams: AccountVestingSearchParamsV2 = {
        ...searchParams,
        ...input,
        type: type ? mapContextTypeToApiType(type) : searchParams.type,
        page: DEFAULT_PAGE_INDEX,
        limit: DEFAULT_PAGE_SIZE,
      }
      setSearchParams(newParams)
      getData(newParams)
    },
    [searchParams, type]
  )

  const initPagination = (totalCount: number) => ({
    total: totalCount,
    current: searchParams.page,
    pageSize: searchParams.limit,
  })

  const onChangePagination = (pagination: TablePaginationConfig) => {
    const { current: newPage, pageSize: newPageSize } = pagination
    const newParams: AccountVestingSearchParamsV2 = {
      ...searchParams,
      page: newPage || DEFAULT_PAGE_INDEX,
      limit: newPageSize || DEFAULT_PAGE_SIZE,
    }
    setSearchParams(newParams)
    getData(newParams)
  }

  useEffect(() => {
    if (type) {
      const apiType = mapContextTypeToApiType(type)
      const params: AccountVestingSearchParamsV2 = { ...searchParams, type: apiType }
      setSearchParams(params)
      getData(params)
    }
  }, [type])

  return {
    loading,
    data,
    total,
    searchParams,
    getData,
    onSearch,
    initPagination,
    onChangePagination,
  }
}
